import { useCallback, ComponentType, ReactNode } from 'react'
import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'

import { media, mediaMax } from 'styles/media'

import useBufferedSlideCount from '../../../hooks/useBufferedSlideCount'
import useEmblaNavigation from '../../../hooks/useEmblaNavigation'

import ChevronDownIcon from '../../icons/ChevronDown'

import Slider from './Slider'
import List from './List'
import SnapList from './SnapList'
import { Arrow, Inner, Header, Title } from './shared'

const BREAKPOINT = 'lg' as const

const MobileOnly = styled.div`
  ${media[BREAKPOINT]`
    display: none;
  `}
`

const DesktopOnly = styled.div`
  ${mediaMax[BREAKPOINT]`
    display: none;
  `}
`

const ArrowsHeader = styled(Header)`
  justify-content: flex-end;
`

const StackedHeader = styled(Header)`
  flex-direction: column;
  align-items: stretch;
  gap: var(--ds-space-04);
`

const HeaderRow = styled(Header).attrs({ as: 'div' })`
  margin-bottom: 0;
`

type PaginatedSlidesHeaderProps = {
  title: ReactNode
  arrows?: ReactNode
  children?: ReactNode
  ArrowsViewport?: ComponentType<{ children?: ReactNode }>
}

export const PaginatedSlidesHeader = ({
  title,
  arrows,
  children,
  ArrowsViewport = DesktopOnly,
}: PaginatedSlidesHeaderProps) => {
  const row = (
    <>
      {title}
      {arrows && <ArrowsViewport>{arrows}</ArrowsViewport>}
    </>
  )

  if (!children) return <Header>{row}</Header>

  return (
    <StackedHeader>
      <HeaderRow>{row}</HeaderRow>
      {children}
    </StackedHeader>
  )
}

const CompactMobileOnly = styled.div`
  ${media.md`
    display: none;
  `}
`

const CompactDesktopOnly = styled.div`
  ${mediaMax.md`
    display: none;
  `}
`

const viewports = {
  default: { Mobile: MobileOnly, Desktop: DesktopOnly, MobileList: List },
  compact: {
    Mobile: CompactMobileOnly,
    Desktop: CompactDesktopOnly,
    MobileList: SnapList,
  },
}

type PaginatedSlidesProps<T> = {
  customTitle?: ReactNode
  hideTitle?: boolean
  called: boolean
  loading: boolean
  canFetchMore: boolean
  onFetchMore: () => any
  items: T[]
  ItemComponent: ComponentType<{ item: T }>
  loadingPlaceholder?: ReactNode
  minHeight?: string
  variant?: keyof typeof viewports
}

const PaginatedSlides = <T extends Record<'id', string>>({
  customTitle,
  hideTitle = false,
  called,
  loading,
  canFetchMore,
  onFetchMore,
  items,
  ItemComponent,
  loadingPlaceholder,
  minHeight,
  variant = 'default',
}: PaginatedSlidesProps<T>) => {
  const compact = variant === 'compact'
  const { Mobile, Desktop, MobileList } = viewports[variant]
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: compact ? 5 : 3,
    containScroll: 'trimSnaps',
  })

  const onReachEnd = useCallback(() => {
    if (canFetchMore && !loading) onFetchMore()
  }, [canFetchMore, loading, onFetchMore])

  const { handlePrev, handleNext, prevDisabled, nextDisabled, singlePage } =
    useEmblaNavigation(emblaApi, { onReachEnd })

  // With everything on one snap and nothing left to fetch, the arrows can
  // never do anything — drop them rather than render a disabled pair.
  const hideArrows = singlePage && !canFetchMore

  const totalSlides = items?.length || 0
  const slideCount = useBufferedSlideCount(totalSlides, emblaApi)
  const paginationNotReady = !called || loading || !items

  const arrows = (
    <>
      <Arrow
        left
        onClick={handlePrev}
        disabled={paginationNotReady || prevDisabled}
        aria-label="Previous"
      >
        <ChevronDownIcon />
      </Arrow>
      <Arrow
        right
        onClick={handleNext}
        disabled={paginationNotReady || nextDisabled}
        aria-label="Next"
      >
        <ChevronDownIcon />
      </Arrow>
    </>
  )

  return (
    <Inner $minHeight={paginationNotReady ? minHeight : 'auto'}>
      {!hideTitle && (
        <PaginatedSlidesHeader
          title={customTitle || <Title>More Escapes</Title>}
          arrows={!hideArrows && arrows}
          ArrowsViewport={Desktop}
        />
      )}
      {hideTitle && !hideArrows && (
        <Desktop>
          <ArrowsHeader>{arrows}</ArrowsHeader>
        </Desktop>
      )}

      <Desktop>
        <Slider
          compact={compact}
          emblaRef={emblaRef}
          items={items || []}
          slideCount={slideCount}
          ItemComponent={ItemComponent}
          loadingPlaceholder={loadingPlaceholder}
        />
      </Desktop>

      <Mobile>
        <MobileList
          loading={loading}
          canFetchMore={canFetchMore}
          onFetchMore={onFetchMore}
          items={items || []}
          ItemComponent={ItemComponent}
          loadingPlaceholder={loadingPlaceholder}
        />
      </Mobile>
    </Inner>
  )
}

export default PaginatedSlides
